import React from "react";
import { OverlayTrigger, Popover } from "react-bootstrap";
import { FluentProvider, webLightTheme } from "@fluentui/react-components";
import TaskMemberAvatarPill from "./TaskMemberAvatarPill";
import { displayName, unavailableReason } from "./TaskAssignMemberHelper";
import { FluentTooltip } from "../FluentUI/Fluent-Tooltip";
import removeAttachment from "../../assets/icons/Icon-Remove-Attachment-Blue-10px.svg";
import PropTypes from "prop-types";

const AssignedMemberPills = ({
  members = [],
  permission,
  disablePill,
  onRemove,
  ownerLabelClassName = "taskUserListTitle",
  isDisabled = false,
}) => {
  return members
    .filter(user => user?.Id)
    .map(user => (
      <div key={user.Id}>
        <div
          className={`small d-flex d-flex-row pill-fill-container ${
            disablePill ? "pills-gray-background" : "pills-blue-background"
          } ${user?.Archived ? "pill-archived" : ""}`}
          data-testid={user?.Name}
          title={user?.Archived && user.IsGroup ? unavailableReason(user) : undefined}
        >
          {/* Archived groups carry no roster — the "Group Members" popover would
              be blank; the container title carries the reason instead (QA
              feedback, TAD-5898). */}
          {user.IsGroup && !user?.Archived ? (
            <OverlayTrigger
              trigger={["hover", "focus"]}
              placement="bottom"
              delay={{ show: 150, hide: 150 }}
              overlay={
                <Popover
                  id="description-popover"
                  className="assignMemberContainer custom-popover-group"
                >
                  <Popover.Title>Group Members</Popover.Title>
                  <Popover.Content>
                    <FluentProvider theme={webLightTheme}>
                      {(user.Members || []).map(member => (
                        <div key={member.User.Id} className="d-flex m-1">
                          <TaskMemberAvatarPill
                            user={member.User}
                            permission={permission}
                            size={24}
                          />
                          <span
                            className={`w-8 pl-1 pr-1 ${ownerLabelClassName} pull-left`}
                          >
                            {member.User?.Name}
                          </span>
                        </div>
                      ))}
                    </FluentProvider>
                  </Popover.Content>
                </Popover>
              }
            >
              <div className="d-flex">
                <TaskMemberAvatarPill user={user} permission={permission} />
                <span
                  className={`w-8 pl-2 pr-1 ${ownerLabelClassName} pull-left`}
                >
                  {displayName(user)}
                </span>
              </div>
            </OverlayTrigger>
          ) : !user.IsGroup && user?.Archived ? (
            <FluentTooltip
              text={unavailableReason(user)}
              target={
                <div className="d-flex">
                  <TaskMemberAvatarPill user={user} permission={permission} />
                  <span
                    className={`w-8 pl-2 pr-1 ${ownerLabelClassName} pull-left`}
                  >
                    {user?.Name?.length > 20
                      ? `${user?.Name.slice(0, 20)}...`
                      : user?.Name}
                  </span>
                </div>
              }
            />
          ) : (
            <>
              <TaskMemberAvatarPill user={user} permission={permission} />
              <span
                className={`w-8 pl-2 pr-1 ${ownerLabelClassName} pull-left`}
              >
                {user?.Name?.length > 20
                  ? `${user?.Name.slice(0, 20)}...`
                  : user?.Name}
              </span>
            </>
          )}

          <button
            type="button"
            className="button-background-removeMember"
            aria-label="Remove member"
            onClick={() => !isDisabled && !disablePill && onRemove(user)}>
            <img src={removeAttachment} alt="Remove member" className={`${disablePill ? "cross-button-disabled" : "pointer"} cross-button`}/>
          </button>
        </div>
      </div>
    ));
};

export default AssignedMemberPills;

AssignedMemberPills.propTypes = {
    members: PropTypes.arrayOf(
      PropTypes.shape({
        Id: PropTypes.oneOfType([PropTypes.string, PropTypes.number]).isRequired,
        Name: PropTypes.string.isRequired,
        IsGroup: PropTypes.bool,
        Members: PropTypes.arrayOf(
          PropTypes.shape({
            User: PropTypes.shape({
              Id: PropTypes.oneOfType([PropTypes.string, PropTypes.number]).isRequired,
              Name: PropTypes.string.isRequired
            }).isRequired
          })
        )
      })
    ),
    permission: PropTypes.oneOf(['owner', 'editor', 'viewer', 'approver']).isRequired,
    disablePill: PropTypes.bool,
    onRemove: PropTypes.func.isRequired,
    ownerLabelClassName: PropTypes.string,
    isDisabled: PropTypes.bool
  };
  
  AssignedMemberPills.defaultProps = {
    members: [],
    disablePill: false,
    ownerLabelClassName: 'taskUserListTitle',
    isDisabled: false
  };